// src/App.js
import React, { Component } from 'react';
import './styles/global.css';
import './styles/auth.css';
import AuthPage         from './components/AuthPage';
import { DoctorLoginPage   } from './components/doctorLogin';
import { DoctorQRScanPage } from './components/DoctorQRScanPage';
import QRScanPage  from './components/QRScanPage';
import { PatientDashboard } from './components/patient/index';
import { DoctorDashboard  } from './components/doctor/doctor';
import { LabDashboard     } from './components/Lab/lab';
import { AdminDashboard   } from './components/Admin/admin';


const ROLE_PATHS = {
  patient:  '/patientDashboard',
  admin:    '/adminDashboard',
  doctor:   '/doctorDashboard',
  lab:      '/labDashboard',
  pharmacy: '/pharmacyDashboard',
};
 
const DOCTOR_LOGIN_PATH  = '/doctorLogin';
const DOCTOR_SCAN_PATH   = '/doctorScan';
 
/* ── Get current URL path ───────────────────────────────────── */
function getPath() {
  return window.location.pathname;
}
 
/* ── Restore session from localStorage ─────────────────────── */
function getSavedUser() {
  try {
    const raw = localStorage.getItem('dg_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
 
/* ── Fallback for roles without a dashboard yet ─────────────── */
class FallbackDashboard extends Component {
  render() {
    const { user, onLogout } = this.props;
    const ROLE_META = {
      patient:  { emoji: '🧑',  label: 'Patient',        color: '#0A6EFF' },
      admin:    { emoji: '🛡️',  label: 'Administrator',  color: '#1a56ff' },
      doctor:   { emoji: '👨‍⚕️', label: 'Doctor',         color: '#00a572' },
      lab:      { emoji: '🔬',  label: 'Lab Technician', color: '#8b5cf6' },
      pharmacy: { emoji: '💊',  label: 'Pharmacist',     color: '#ef4444' },
    };
    const role = (user?.role || '').toLowerCase();
    const meta = ROLE_META[role] || { emoji: '👤', label: 'User', color: '#6b748c' };
 
    return (
      <div style={{ minHeight:'100vh', background:'#f7f9fc', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Outfit,sans-serif', padding:24 }}>
        <div style={{ background:'#fff', borderRadius:20, padding:'48px 40px', textAlign:'center', maxWidth:440, width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,.1)', border:'1px solid #e2e8f0' }}>
          <div style={{ fontSize:56, marginBottom:16 }}>{meta.emoji}</div>
          <div style={{ fontSize:22, fontWeight:800, color:'#0a0f1e', marginBottom:8 }}>Welcome, {user?.name}!</div>
          <div style={{ fontSize:14, color:'#6b748c', marginBottom:24, lineHeight:1.6 }}>
            Logged in as <strong style={{ color:meta.color }}>{meta.label}</strong>.<br />Dashboard coming soon.
          </div>
          <div style={{ background:`${meta.color}10`, border:`1px solid ${meta.color}28`, borderRadius:12, padding:'14px 18px', marginBottom:24, fontSize:13, color:'#2d3452', display:'flex', flexDirection:'column', gap:8 }}>
            {[
              { k:'Name',  v: user?.name  },
              { k:'Email', v: user?.email },
              { k:'Role',  v: meta.label  },
              { k:'URL',   v: window.location.pathname },
            ].map((row, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'#6b748c' }}>{row.k}</span>
                <span style={{ fontWeight:700, color:meta.color }}>{row.v}</span>
              </div>
            ))}
          </div>
          <button onClick={onLogout} style={{ width:'100%', height:48, border:'1.5px solid #dde2f0', borderRadius:12, background:'#fff', color:'#2d3452', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'Outfit,sans-serif' }}>
            ← Sign Out
          </button>
        </div>
      </div>
    );
  }
}
 
/* ═══════════════════════════════════════════════════════════════
   ROOT APP  — no react-router-dom needed
   Uses window.history.pushState for clean URLs
═══════════════════════════════════════════════════════════════ */
class App extends Component {
  constructor(props) {
    super(props);
 
    const savedUser = getSavedUser();
    const path      = getPath();
 
    // If user is saved and at /login or /, redirect to their dashboard
    let currentPath = path;
    if (savedUser && (path === '/' || path === '/login')) {
      const rolePath = ROLE_PATHS[(savedUser.role || '').toLowerCase().trim()];
      if (rolePath) {
        window.history.replaceState({}, '', rolePath);
        currentPath = rolePath;
      }
    }
 
    // /scan route — public QR scanner, no login needed
    if (path === '/scan' || path.startsWith('/scan')) {
      currentPath = '/scan';
    }
    // /patientDashboard or /guest — open as guest without login
    else if ((path === '/patientDashboard' || path === '/guest') && !savedUser) {
      const guestUser = {
        personId:  null,
        name:      'Guest Patient',
        email:     '',
        role:      'patient',
        isGuest:   true,
      };
      window.history.replaceState({}, '', '/patientDashboard');
      currentPath = '/patientDashboard';
      this._guestUser = guestUser;
    }
    // If path is /doctorLogin, show doctor login page
    if (path === DOCTOR_LOGIN_PATH && !savedUser) {
      this.state = { ...this.state, showDoctorLogin: true };
      window.history.pushState({}, '', DOCTOR_LOGIN_PATH);
    }
 
    // Public paths — never redirect to login
    const PUBLIC_PATHS = ['/scan', '/guest', '/patientDashboard', DOCTOR_LOGIN_PATH, DOCTOR_SCAN_PATH];
    const isPublic = PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '?'));
 
    // If no user and on a protected path, redirect to /login
    if (!savedUser && !isPublic && path !== '/login' && path !== '/') {
      window.history.replaceState({}, '', '/login');
      currentPath = '/login';
    }
 
    this.state = {
      user:        this._guestUser || savedUser,
      currentPath: currentPath,
    };
 
    // Listen to browser back/forward buttons
    window.onpopstate = () => {
      this.setState({ currentPath: getPath() });
    };
  }
 
  navigate(path) {
    window.history.pushState({}, '', path);
    this.setState({ currentPath: path });
  }
 
  handleLogin(user) {
    // Guest users — do NOT persist to localStorage (session only)
    if (!user.isGuest) {
      localStorage.setItem('dg_user', JSON.stringify(user));
    }
    this.setState({ user });
 
    const rolePath = ROLE_PATHS[(user.role || '').toLowerCase().trim()] || '/login';
    this.navigate(rolePath);
  }
 
  handleLogout() {
    localStorage.removeItem('dg_token');
    localStorage.removeItem('dg_user');
    this.setState({ user: null });
    this.navigate('/login');
  }
 
  renderDashboard() {
    const { user, currentPath } = this.state;
    // Use actual URL as fallback in case currentPath is stale
    const path = currentPath || window.location.pathname;
 
    // Patient
    if (path === '/patientDashboard') {
      return (
        <PatientDashboard
          user={user}
          onLogout={() => this.handleLogout()}
          onLogin={u => this.handleLogin(u)}
        />
      );
    }
 
    // Doctor
    if (path === '/doctorDashboard') {
      return <DoctorDashboard user={user} onLogout={() => this.handleLogout()} />;
    }
 
    // Lab
    if (path === '/labDashboard') {
      return <LabDashboard user={user} onLogout={() => this.handleLogout()} />;
    }
 
    // Admin
    if (path === '/adminDashboard') {
      return <AdminDashboard user={user} onLogout={() => this.handleLogout()} />;
    }
 
    // Pharmacy — uncomment when ready
    // if (currentPath === '/pharmacyDashboard') {
    //   return <PharmaDashboard user={user} onLogout={() => this.handleLogout()} />;
    // }
 
    // Fallback for all other role paths
    return (
      <FallbackDashboard
        user={user}
        onLogout={() => this.handleLogout()}
      />
    );
  }
 
  render() {
    const { user, currentPath } = this.state;
 
    // ── Public QR scan page — no login required ────────────────
    if (currentPath === '/scan') {
      return <QRScanPage />;
    }
 
    // ── /patientDashboard always accessible — show as guest ─────
    if (currentPath === '/patientDashboard' && !user) {
      const guestUser = { personId: null, name: 'Guest Patient', email: '', role: 'patient', isGuest: true };
      return (
        <PatientDashboard
          user={guestUser}
          onLogout={() => this.navigate('/login')}
          onLogin={u => this.handleLogin(u)}
        />
      );
    }
 
    // ── No session → always show login ──────────────────────────
    if (!user) {
      // Check path OR state to show doctor login
      if (currentPath === DOCTOR_SCAN_PATH || window.location.pathname === DOCTOR_SCAN_PATH) {
        return (
          <DoctorQRScanPage
            onLogin={u => this.handleLogin(u)}
            onBack={() => window.history.pushState({}, '', '/login')}
          />
        );
      }
      if (currentPath === DOCTOR_LOGIN_PATH || this.state.showDoctorLogin) {
        return (
          <DoctorLoginPage
            onLogin={u => { this.setState({ showDoctorLogin: false }); this.handleLogin(u); }}
            onBack={() => { this.setState({ showDoctorLogin: false }); window.history.pushState({}, '', '/login'); }}
          />
        );
      }
      return <AuthPage onLogin={u => this.handleLogin(u)} onDoctorLogin={() => { this.setState({ showDoctorLogin: true }); window.history.pushState({}, '', DOCTOR_LOGIN_PATH); }} />;
    }
 
    // ── Session exists + visiting / or /login → go to dashboard ─
    if (currentPath === '/' || currentPath === '/login') {
      const rolePath = ROLE_PATHS[(user.role || '').toLowerCase().trim()];
      if (rolePath) {
        // Update URL silently then render dashboard
        window.history.replaceState({}, '', rolePath);
      }
      return this.renderDashboard();
    }
 
    // ── Session exists + on a dashboard path → render dashboard ─
    return this.renderDashboard();
  }
}
 
export default App;